import React, { useState } from "react";
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AppsIcon from "@material-ui/icons/Apps";
import ViewComfyIcon from "@material-ui/icons/ViewComfy";
import ViewModuleIcon from "@material-ui/icons/ViewModule";
import ViewCompactIcon from "@material-ui/icons/ViewCompact";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import CheckIcon from "@material-ui/icons/Check";

export const GRID_LAYOUTS = [
  {
    id: "6x5",
    label: "6 × 5",
    cols: 6,
    rows: 5,
    description: "6 cards / row",
    Icon: AppsIcon,
  },
  {
    id: "5x4",
    label: "5 × 4",
    cols: 5,
    rows: 4,
    description: "5 cards / row (Default)",
    Icon: ViewComfyIcon,
  },
  {
    id: "4x4",
    label: "4 × 4",
    cols: 4,
    rows: 4,
    description: "4 cards / row",
    Icon: ViewModuleIcon,
  },
  {
    id: "3x3",
    label: "3 × 3",
    cols: 3,
    rows: 3,
    description: "3 cards / row",
    Icon: ViewCompactIcon,
  },
];

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    marginLeft: theme.spacing(1),
    // Auto-hide on mobile and tablets (< 960px)
    [theme.breakpoints.down("sm")]: {
      display: "none !important",
    },
  },
  gridButton: {
    display: "flex",
    alignItems: "center",
    height: 38,
    padding: theme.spacing(0.5, 1.25),
    backgroundColor: "#ffffff",
    color: "#1e293b",
    border: "1.5px solid rgba(0, 0, 0, 0.08)",
    borderRadius: 10,
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.8125rem",
    whiteSpace: "nowrap",
    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.06)",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#ffffff",
      borderColor: "rgba(0, 0, 0, 0.18)",
      color: "#ea580c",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    },
  },
  buttonText: {
    fontWeight: 700,
    fontSize: "0.8125rem",
    margin: theme.spacing(0, 0.5),
    letterSpacing: "0.02em",
  },
  activeIcon: {
    fontSize: 18,
    color: "#ef7f1a",
  },
  arrowIcon: {
    fontSize: 18,
    color: "#64748b",
    transition: "transform 0.2s ease",
  },
  arrowIconOpen: {
    transform: "rotate(180deg)",
  },
  menuPaper: {
    borderRadius: 12,
    border: "1px solid #e2e8f0",
    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.08)",
    minWidth: 200,
    padding: theme.spacing(0.5, 0),
  },
  menuHeader: {
    padding: theme.spacing(1, 2, 0.5, 2),
    fontSize: "0.7rem",
    fontWeight: 700,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  menuItem: {
    padding: theme.spacing(1, 1.5),
    borderRadius: 8,
    margin: theme.spacing(0.25, 0.75),
    transition: "all 0.15s ease",
    "&:hover": {
      backgroundColor: "#fff7ed",
      "& $itemLabel": {
        color: "#ea580c",
      },
    },
    "&.Mui-selected": {
      backgroundColor: "#ffedd5 !important",
      "& $itemLabel": {
        color: "#c2410c",
        fontWeight: 700,
      },
      "&:hover": {
        backgroundColor: "#fed7aa !important",
      },
    },
  },
  itemIconWrapper: {
    minWidth: 32,
    color: "#64748b",
  },
  itemIconWrapperSelected: {
    color: "#ef7f1a",
  },
  itemLabel: {
    fontSize: "0.8125rem",
    fontWeight: 600,
    color: "#1e293b",
  },
  itemDesc: {
    fontSize: "0.7rem",
    color: "#64748b",
  },
  checkIcon: {
    fontSize: 16,
    color: "#ef7f1a",
    marginLeft: theme.spacing(1),
  },
}));

export default function GridLayoutSelector({
  gridLayout = "5x4",
  onGridLayoutChange,
}) {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const currentLayout =
    GRID_LAYOUTS.find((layout) => layout.id === gridLayout) || GRID_LAYOUTS[0];
  const CurrentIcon = currentLayout.Icon;

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleSelect = (layoutId) => {
    if (onGridLayoutChange) {
      onGridLayoutChange(layoutId);
    }
    handleCloseMenu();
  };

  return (
    <div className={classes.root}>
      <Tooltip title={`Grid Layout: ${currentLayout.label} (${currentLayout.description})`} arrow>
        <Button
          className={classes.gridButton}
          onClick={handleOpenMenu}
          aria-haspopup="true"
          aria-expanded={isMenuOpen}
          aria-label="Change item card grid layout"
        >
          <CurrentIcon className={classes.activeIcon} />
          <span className={classes.buttonText}>{currentLayout.label}</span>
          <ArrowDropDownIcon
            className={`${classes.arrowIcon} ${isMenuOpen ? classes.arrowIconOpen : ""}`}
          />
        </Button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={isMenuOpen}
        onClose={handleCloseMenu}
        classes={{ paper: classes.menuPaper }}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Typography className={classes.menuHeader}>Card Grid Layout</Typography>
        {GRID_LAYOUTS.map((layout) => {
          const isSelected = layout.id === currentLayout.id;
          const OptionIcon = layout.Icon;
          return (
            <MenuItem
              key={layout.id}
              selected={isSelected}
              onClick={() => handleSelect(layout.id)}
              className={classes.menuItem}
            >
              <ListItemIcon
                className={`${classes.itemIconWrapper} ${
                  isSelected ? classes.itemIconWrapperSelected : ""
                }`}
              >
                <OptionIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={<span className={classes.itemLabel}>{layout.label}</span>}
                secondary={<span className={classes.itemDesc}>{layout.description}</span>}
              />
              {isSelected && <CheckIcon className={classes.checkIcon} />}
            </MenuItem>
          );
        })}
      </Menu>
    </div>
  );
}
